export const RUNTIME_HEARTBEAT_INTERVAL_MS = 2_000;
export const PREVIEW_RUNTIME_STARTUP_ERROR_WINDOW_MS = 10_000;
export const PREVIEW_CANDIDATE_PROMOTION_DELAY_MS = PREVIEW_RUNTIME_STARTUP_ERROR_WINDOW_MS + 250;
export const RUNTIME_READY_MESSAGE_TYPE = "applyt:runtime-ready";
export const RUNTIME_STABLE_MESSAGE_TYPE = "applyt:runtime-stable";
export const RUNTIME_ROUTE_NOT_FOUND_MESSAGE_TYPE = "applyt:runtime-route-not-found";
export const RUNTIME_ERROR_MESSAGE_TYPE = "applyt:runtime-error";
export const RUNTIME_HEARTBEAT_MESSAGE_TYPE = "applyt:runtime-heartbeat";

export const RUNTIME_READY_SCRIPT = `<script data-applyt-runtime-ready="true">
(function(){
  if (window.__APPLYT_RUNTIME_READY_INSTALLED__) return;
  window.__APPLYT_RUNTIME_READY_INSTALLED__ = true;
  var startupErrorWindowMs = ${PREVIEW_RUNTIME_STARTUP_ERROR_WINDOW_MS};
  var done = false;
  var readyAt = 0;
  var stable = false;
  var routeNotFound = false;
  var runtimeError = false;
  var observer = null;
  var stabilityTimer = null;
  var heartbeatTimer = null;
  var layoutMissingSince = null;
  var layoutErrorReported = false;
  function post(type, extra){
    try { window.parent.postMessage(Object.assign({ type: type }, extra || {}), "*"); } catch (_error) {}
  }
  function isEarlyRuntimeErrorWindow(){
    return !done || (!stable && readyAt > 0 && Date.now() - readyAt <= startupErrorWindowMs);
  }
  function hasRuntimeRouteNotFound(){
    try {
      if (document.querySelector('[data-source-location^="PageNotFound:"]')) return true;
      var text = ((document.body && document.body.innerText) || "").trim();
      return /\\bPage Not Found\\b/i.test(text) && /could not be found in this application/i.test(text);
    } catch (_error) {
      return false;
    }
  }
  function hasDocumentLayout(){
    try {
      var rects = document.documentElement.getClientRects();
      // A fixed-position App can leave the document's flow height at zero.
      // Missing layout has no client boxes at all, including for the root.
      return rects.length > 0 && rects[0].width > 0;
    } catch (_error) {
      return false;
    }
  }
  function hasRuntimeContent(){
    var body = document.body;
    if (!body) return false;
    var root = document.getElementById("root") || document.getElementById("app") || body;
    if (!root || root.children.length === 0) return false;
    var text = "";
    try { text = (root.innerText || body.innerText || "").trim(); } catch (_error) {}
    if (text.length > 0) return true;
    return !!root.querySelector("canvas,svg,img,button,input,textarea,select,[role],[data-testid]");
  }
  function hasRenderedContent(){
    // innerText falls back to DOM text when Chrome has no layout tree. That
    // text must not hide the connecting overlay or promote a cached candidate.
    return hasDocumentLayout() && hasRuntimeContent();
  }
  function stopHeartbeat(){
    if (!heartbeatTimer) return;
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  function postHeartbeat(){
    if (!done) {
      signalReady();
      return;
    }
    post("${RUNTIME_HEARTBEAT_MESSAGE_TYPE}", { visible: hasRenderedContent() });
  }
  function signalRouteNotFound(){
    if (routeNotFound || runtimeError || stable) return;
    routeNotFound = true;
    if (stabilityTimer) clearTimeout(stabilityTimer);
    stopHeartbeat();
    post("${RUNTIME_ROUTE_NOT_FOUND_MESSAGE_TYPE}", { pathname: window.location && window.location.pathname || "" });
    if (observer) observer.disconnect();
  }
  function safeResourceDetail(target){
    var tag = String(target && target.tagName || "").toUpperCase();
    var raw = tag === "SCRIPT" ? target.src : tag === "LINK" ? target.href : "";
    var extension = "unknown";
    var sameOrigin = null;
    try {
      var parsed = new URL(raw, window.location.href);
      var match = parsed.pathname.match(/\\.([a-z0-9]{1,8})$/i);
      if (match) extension = match[1].toLowerCase();
      sameOrigin = parsed.origin === window.location.origin;
    } catch (_error) {}
    return { resourceType: tag.toLowerCase(), resourceExtension: extension, sameOrigin: sameOrigin };
  }
  function signalRuntimeError(phase, detail){
    if (runtimeError || routeNotFound || !isEarlyRuntimeErrorWindow()) return;
    runtimeError = true;
    if (stabilityTimer) clearTimeout(stabilityTimer);
    stopHeartbeat();
    post("${RUNTIME_ERROR_MESSAGE_TYPE}", Object.assign({ phase: phase || (done ? "after_ready" : "before_ready") }, detail || {}));
    if (observer) observer.disconnect();
  }
  function signalStable(){
    stabilityTimer = null;
    if (runtimeError || routeNotFound || !done || stable) return;
    if (hasRuntimeRouteNotFound()) {
      signalRouteNotFound();
      return;
    }
    if (!hasRenderedContent()) {
      postHeartbeat();
      stabilityTimer = setTimeout(signalStable, ${RUNTIME_HEARTBEAT_INTERVAL_MS});
      return;
    }
    stable = true;
    post("${RUNTIME_STABLE_MESSAGE_TYPE}");
  }
  function signalReady(){
    if (runtimeError || routeNotFound) return;
    if (done) return;
    if (hasRuntimeRouteNotFound()) {
      signalRouteNotFound();
      return;
    }
    if (!hasRenderedContent()) {
      if (hasRuntimeContent() && !hasDocumentLayout()) {
        if (layoutMissingSince === null) layoutMissingSince = Date.now();
        if (!layoutErrorReported && Date.now() - layoutMissingSince >= ${RUNTIME_HEARTBEAT_INTERVAL_MS * 2}) {
          layoutErrorReported = true;
          // Missing layout can recover when Chrome resumes rendering a frame.
          // Ask the host for bounded recovery but keep this document's probe
          // alive so the final retry can become ready without another remount.
          post("${RUNTIME_ERROR_MESSAGE_TYPE}", { phase: "before_ready", errorName: "RuntimeLayoutError" });
        }
      } else {
        layoutMissingSince = null;
      }
      return;
    }
    layoutMissingSince = null;
    done = true;
    readyAt = Date.now();
    post("${RUNTIME_READY_MESSAGE_TYPE}");
    postHeartbeat();
    if (!heartbeatTimer) heartbeatTimer = setInterval(postHeartbeat, ${RUNTIME_HEARTBEAT_INTERVAL_MS});
    if (observer) observer.disconnect();
    stabilityTimer = setTimeout(signalStable, ${PREVIEW_CANDIDATE_PROMOTION_DELAY_MS});
  }
  function start(){
    signalReady();
    // Keep checking after DOM mutations stop: a document can have content
    // before Chrome creates its layout, or lose layout after its first paint.
    if (!runtimeError && !routeNotFound && !heartbeatTimer) heartbeatTimer = setInterval(postHeartbeat, ${RUNTIME_HEARTBEAT_INTERVAL_MS});
    if (done || runtimeError || routeNotFound || !document.documentElement) return;
    observer = new MutationObserver(signalReady);
    observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
    setTimeout(signalReady, 50);
    setTimeout(signalReady, 300);
    setTimeout(signalReady, 1000);
    setTimeout(signalReady, 3000);
  }
  window.addEventListener("error", function(event){
    try {
      var target = event && event.target;
      if (target && target !== window) {
        var tag = String(target.tagName || "").toUpperCase();
        if (tag === "SCRIPT" || tag === "LINK") signalRuntimeError("resource_error", safeResourceDetail(target));
        return;
      }
      if (event && event.message) signalRuntimeError("script_error", {
        errorName: String(event.error && event.error.name || "Error").replace(/[^A-Za-z]/g, "").slice(0, 40),
        line: Number.isSafeInteger(event.lineno) ? event.lineno : null,
        column: Number.isSafeInteger(event.colno) ? event.colno : null,
      });
    } catch (_error) {}
  }, true);
  window.addEventListener("unhandledrejection", function(event){
    signalRuntimeError("unhandled_rejection", {
      errorName: String(event.reason && event.reason.name || "Error").replace(/[^A-Za-z]/g, "").slice(0, 40),
    });
  });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
</script>`;
export function injectRuntimeReadySignal(html: string): string {
  if (/data-applyt-runtime-ready=(?:"true"|'true')/i.test(html)) return html;
  if (/<head\b[^>]*>/i.test(html)) {
    return html.replace(/<head\b([^>]*)>/i, `<head$1>${RUNTIME_READY_SCRIPT}`);
  }
  if (/<body\b[^>]*>/i.test(html)) {
    return html.replace(/<body\b([^>]*)>/i, `<body$1>${RUNTIME_READY_SCRIPT}`);
  }
  return `${RUNTIME_READY_SCRIPT}${html}`;
}
